import { Node } from "./Node";
import { Edge } from "./Edge";
import { NodeId, EdgeId, SocketId } from "./types";

/**
 * Handles queries and graph analysis operations
 * Separated from main Graph class for better organization
 */
export class GraphQueries {
  constructor(
    private nodes: Map<NodeId, Node>,
    private edges: Map<EdgeId, Edge>,
    private nodesByType: Map<string, Set<NodeId>>,
    private edgesBySource: Map<NodeId, Map<SocketId, Set<EdgeId>>>,
    private edgesByTarget: Map<NodeId, Map<SocketId, Set<EdgeId>>>,
  ) {}

  // ============================================
  // Node Queries
  // ============================================

  /**
   * Get node by ID - O(1)
   */
  getNode(nodeId: NodeId): Node | undefined {
    return this.nodes.get(nodeId);
  }

  /**
   * Check if node exists - O(1)
   */
  hasNode(nodeId: NodeId): boolean {
    return this.nodes.has(nodeId);
  }

  /**
   * Get all nodes
   */
  getNodes(): Node[] {
    return Array.from(this.nodes.values());
  }

  /**
   * Get nodes by type - O(1)
   */
  getNodesByType(type: string): Node[] {
    const nodeIds = this.nodesByType.get(type);
    if (!nodeIds) return [];

    const nodes: Node[] = [];
    for (const nodeId of nodeIds) {
      const node = this.nodes.get(nodeId);
      if (node) nodes.push(node);
    }
    return nodes;
  }

  // ============================================
  // Edge Queries - O(1) optimized
  // ============================================

  /**
   * Get edge by ID - O(1)
   */
  getEdge(edgeId: EdgeId): Edge | undefined {
    return this.edges.get(edgeId);
  }

  /**
   * Check if edge exists - O(1)
   */
  hasEdge(edgeId: EdgeId): boolean {
    return this.edges.has(edgeId);
  }

  /**
   * Get all edges
   */
  getEdges(): Edge[] {
    return Array.from(this.edges.values());
  }

  /**
   * Get all edges connected to a node - O(1)
   */
  getNodeEdges(nodeId: NodeId): Edge[] {
    const inputEdges = this.getNodeInputEdges(nodeId);
    const outputEdges = this.getNodeOutputEdges(nodeId);
    return [...inputEdges, ...outputEdges];
  }

  /**
   * Get input edges for a node - O(1)
   */
  getNodeInputEdges(nodeId: NodeId): Edge[] {
    const nodeEdges = this.edgesByTarget.get(nodeId);
    if (!nodeEdges) return [];

    const edges: Edge[] = [];
    for (const socketEdges of nodeEdges.values()) {
      for (const edgeId of socketEdges) {
        const edge = this.edges.get(edgeId);
        if (edge) edges.push(edge);
      }
    }
    return edges;
  }

  /**
   * Get output edges for a node - O(1)
   */
  getNodeOutputEdges(nodeId: NodeId): Edge[] {
    const nodeEdges = this.edgesBySource.get(nodeId);
    if (!nodeEdges) return [];

    const edges: Edge[] = [];
    for (const socketEdges of nodeEdges.values()) {
      for (const edgeId of socketEdges) {
        const edge = this.edges.get(edgeId);
        if (edge) edges.push(edge);
      }
    }
    return edges;
  }

  /**
   * Get edges connected to a specific socket - O(1)
   */
  getSocketEdges(nodeId: NodeId, socketId: SocketId): Edge[] {
    const edges: Edge[] = [];

    // Check source edges
    const sourceEdges = this.edgesBySource.get(nodeId)?.get(socketId);
    if (sourceEdges) {
      for (const edgeId of sourceEdges) {
        const edge = this.edges.get(edgeId);
        if (edge) edges.push(edge);
      }
    }

    // Check target edges
    const targetEdges = this.edgesByTarget.get(nodeId)?.get(socketId);
    if (targetEdges) {
      for (const edgeId of targetEdges) {
        const edge = this.edges.get(edgeId);
        if (edge) edges.push(edge);
      }
    }

    return edges;
  }

  // ============================================
  // Boundary Analysis
  // ============================================

  // NOTE: DAG-specific methods (getSourceNodes, getSinkNodes, getInputNodes, getOutputNodes)
  // have been moved to DAGQueries service in dag-graph package
  // as they are specific to Directed Acyclic Graphs

  /**
   * Get input nodes (explicit graph-input nodes)
   * @deprecated Use DAGQueries.getInputNodes() for DAG-specific operations
   */
  getInputNodes(): Node[] {
    return this.getNodesByType("graph-input");
  }

  /**
   * Get output nodes (explicit graph-output nodes)
   * @deprecated Use DAGQueries.getOutputNodes() for DAG-specific operations
   */
  getOutputNodes(): Node[] {
    return this.getNodesByType("graph-output");
  }

  // ============================================
  // Graph Traversal
  // ============================================

  /**
   * Get upstream nodes with depth limit (nodes that this node depends on)
   */
  getUpstreamNodes(nodeId: NodeId, maxDepth = Infinity): NodeId[] {
    if (!this.hasNode(nodeId)) {
      return [];
    }

    const upstream: NodeId[] = [];
    const visited = new Set<NodeId>();
    const queue: Array<{ id: NodeId; depth: number }> = [
      { id: nodeId, depth: 0 },
    ];

    while (queue.length > 0) {
      const { id, depth } = queue.shift()!;

      if (visited.has(id) || depth > maxDepth) {
        continue;
      }

      visited.add(id);

      // Don't include the starting node in results
      if (id !== nodeId) {
        upstream.push(id);
      }

      // Get all input edges for this node
      const inputEdges = this.getNodeInputEdges(id);

      for (const edge of inputEdges) {
        const sourceId = edge.source.nodeId;
        if (!visited.has(sourceId) && depth < maxDepth) {
          queue.push({ id: sourceId, depth: depth + 1 });
        }
      }
    }

    return upstream;
  }

  /**
   * Get downstream nodes with depth limit (nodes that depend on this node)
   */
  getDownstreamNodes(nodeId: NodeId, maxDepth = Infinity): NodeId[] {
    if (!this.hasNode(nodeId)) {
      return [];
    }

    const downstream: NodeId[] = [];
    const visited = new Set<NodeId>();
    const queue: Array<{ id: NodeId; depth: number }> = [
      { id: nodeId, depth: 0 },
    ];

    while (queue.length > 0) {
      const { id, depth } = queue.shift()!;

      if (visited.has(id) || depth > maxDepth) {
        continue;
      }

      visited.add(id);

      // Don't include the starting node in results
      if (id !== nodeId) {
        downstream.push(id);
      }

      // Get all output edges for this node
      const outputEdges = this.getNodeOutputEdges(id);

      for (const edge of outputEdges) {
        const targetId = edge.target.nodeId;
        if (!visited.has(targetId) && depth < maxDepth) {
          queue.push({ id: targetId, depth: depth + 1 });
        }
      }
    }

    return downstream;
  }

  // ============================================
  // Cycle Detection
  // ============================================

  // NOTE: DAG-specific methods (detectCycles, topologicalSort)
  // have been moved to DAGQueries service in dag-graph package
  // as they are specific to Directed Acyclic Graphs and don't apply
  // to general graphs which may have cycles (state machines, flow graphs, etc)

  // ============================================
  // Dependency Analysis
  // ============================================

  /**
   * Find all nodes that depend on the given node
   */
  getDependents(nodeId: NodeId): Set<NodeId> {
    const dependents = new Set<NodeId>();
    const visited = new Set<NodeId>();

    const traverse = (currentId: NodeId): void => {
      visited.add(currentId);
      const outputEdges = this.getNodeOutputEdges(currentId);

      for (const edge of outputEdges) {
        const targetId = edge.target.nodeId;
        dependents.add(targetId);

        if (!visited.has(targetId)) {
          traverse(targetId);
        }
      }
    };

    traverse(nodeId);
    return dependents;
  }

  /**
   * Find all nodes that the given node depends on
   */
  getDependencies(nodeId: NodeId): Set<NodeId> {
    const dependencies = new Set<NodeId>();
    const visited = new Set<NodeId>();

    const traverse = (currentId: NodeId): void => {
      visited.add(currentId);
      const inputEdges = this.getNodeInputEdges(currentId);

      for (const edge of inputEdges) {
        const sourceId = edge.source.nodeId;
        dependencies.add(sourceId);

        if (!visited.has(sourceId)) {
          traverse(sourceId);
        }
      }
    };

    traverse(nodeId);
    return dependencies;
  }
}
